<template>
    <DsPopover
        v-if="isUserMenuVisible"
        v-model:open="isMenuOpen"
    >
        <DsPopoverTrigger>
            <button
                class="flex items-center justify-center bg-transparent p-3 pl-5"
                type="button"
            >
                <DsAvatar.Initials
                    class="bg-ds-primary-900 text-white"
                    :firstName="currentUserService?.currentUser?.name?.split(' ')[0] ?? 'U'"
                    size="md"
                />

                <span
                    class="hidden max-w-56 truncate px-1 text-content-md font-medium capitalize text-content-primary legacy-md:inline-block"
                >
                    {{ currentUserService?.currentUser?.name?.split(' ')[0] }}
                </span>
                <DsIcon
                    class="hidden legacy-md:block"
                    icon="fa-solid fa-chevron-down"
                    size="sm"
                    :style="{
                        transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease-in-out',
                        transformOrigin: 'center',
                    }"
                />
            </button>
        </DsPopoverTrigger>
        <DsPopoverContent
            align="end"
            class="w-72"
            side="bottom"
            @openAutoFocus.prevent
        >
            <div class="p-2">
                <div class="mb-2 grid place-items-center rounded bg-ds-cream-100 p-4 text-center">
                    <DsAvatar.Initials
                        :firstName="currentUserService?.currentUser?.name?.split(' ')[0] ?? 'U'"
                        :lastName="currentUserService?.currentUser?.name?.split(' ')[1]"
                        size="md"
                    />
                    <p class="mt-1 break-words text-content-lg font-semibold text-content-primary">
                        {{ currentUserService?.currentUser?.name }}
                    </p>
                    <p class="break-all text-content-sm text-content-secondary">
                        {{ currentUserService?.currentUser?.email }}
                    </p>
                </div>

                <DsMenuOption
                    v-if="isUserSettingsVisible"
                    :href="userStore.userSettingsLink"
                    leading="fa-regular fa-gear"
                >
                    User settings
                </DsMenuOption>

                <DsMenuOption
                    leading="fa-regular fa-arrow-right-from-bracket"
                    @click="currentUserService.logout()"
                >
                    Logout
                </DsMenuOption>
            </div>
            <div class="grid place-items-center border-t border-ds-primary-200 py-4 text-content-sm text-content-primary">
                <div class="mb-2 flex items-center gap-2">
                    <a
                        class="rounded underline hover:no-underline focus:ring-2 focus:ring-ds-primary-300"
                        href="https://www.closinglock.com/terms-company"
                        target="_blank"
                    >
                        Terms of service
                    </a>
                    •
                    <a
                        class="rounded underline hover:no-underline focus:ring-2 focus:ring-ds-primary-300"
                        href="https://www.closinglock.com/privacy-policy"
                        target="_blank"
                    >
                        Privacy policy
                    </a>
                </div>
                <p class="text-[10px] text-content-secondary">
                    © {{ new Date().getFullYear() }} Closinglock, Inc. All rights reserved.
                </p>
            </div>
        </DsPopoverContent>
    </DsPopover>
</template>

<script setup lang="ts">
import DsAvatar from '@/components/design-system/avatar';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsMenuOption from '@/components/design-system/menu/DsMenuOption.vue';
import DsPopover from '@/components/design-system/popover/DsPopover.vue';
import DsPopoverContent from '@/components/design-system/popover/DsPopoverContent.vue';
import DsPopoverTrigger from '@/components/design-system/popover/DsPopoverTrigger.vue';
import { useCurrentUserService } from '@/services/current-user.service';
import { useIsLoginProcessRoute } from '@/shared/utils/login-process-route';
import { useFeatureFlagsStore } from '@/stores/company/feature-flags.store';
import { useUserStore } from '@/stores/company/user.store';
import { useEventListener } from '@vueuse/core';
import { computed, ref } from 'vue';

const isMenuOpen = ref(false);

const isLoginProcessRoute = useIsLoginProcessRoute();
const isUserMenuVisible = computed(() => !isLoginProcessRoute.value && currentUserService.currentUser?.role);

const currentUserService = useCurrentUserService();
const userStore = useUserStore();
const featureFlagsStore = useFeatureFlagsStore();

const isUserSettingsVisible = computed(
    () => userStore.allowChangePassword || featureFlagsStore.flags?.two_way_text_email_notifications,
);

useEventListener('resize', () => {
    if (isMenuOpen.value) {
        isMenuOpen.value = false;
    }
});
</script>
